import { GridColumn, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { TextBox } from '@progress/kendo-react-inputs';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Subject, debounceTime } from 'rxjs';
import { CoordinatesTextShort, KendoGrid } from '../../components/common';
import { useFormatter } from '../../hooks';
import { ICameraFilter, ICameraItem, ICustomer, ILocation, ILookupValue, StreamSourceType } from '../../model';
import { camerasService, locationsService } from '../../services';
import { cameraFilterParser } from '../customers/camera-filter-parser';
import styles from './CamerasSection.module.scss';
import { useAppSelector } from '../../store/hooks';
import { selectAppSettings } from '../../features';
import {ILocationCustomerLookupValue} from "../../model/api/locations/location-customer-lookup-value";

export interface ICamerasState {
  data: Array<ICameraItem>;
  total: number;
}

const defaultPaginator = {
  skip: 0,
  take: 20,
};

export const CamerasSection: FC<{ customer?: ICustomer | ILocationCustomerLookupValue | undefined; location?: ILocation }> = ({ customer, location }) => {
  const formatter = useFormatter();
  const [queryText, setQueryText] = useState<string>();
  const loc = useLocation();
  const [camerasState, setCamerasState] = useState<ICamerasState>({
    data: [],
    total: 0,
  });
  const [locations, setLocations] = useState<ILookupValue[]>([]);
  const [filter, setFilter] = useState<ICameraFilter>();
  const filterRef = useRef<ICameraFilter | null>();
  filterRef.current = filter;

  const settings = useAppSelector(selectAppSettings);

  const onSearch$ = useMemo(() => new Subject<string | undefined>(), []);
  useEffect(() => {
    const subscription = onSearch$.pipe(debounceTime(800)).subscribe((x) => {
      setFilter({ ...filterRef.current, ...defaultPaginator, query: x });
    });
    return () => subscription.unsubscribe();
  }, [onSearch$]);

  useEffect(() => {
    let filter = cameraFilterParser.parse(loc.search);
    if (location) {
      setLocations([location]);
      filter = { ...filter, locationIds: [location.uniqueId] };
      setQueryText(filter.query);
      setFilter(filter);
    } else if (customer) {
      locationsService.getListByCustomerId(customer.id).then((x) => {
        setLocations(x);
        filter = { ...filter, customer: customer.uniqueId };
        setQueryText(filter.query);
        setFilter(filter);
      });
    }
  }, []);

  useEffect(() => {
    if (!filter) {
      return;
    }

    camerasService.getList(filter).then((camerasData) => {
      setCamerasState({ data: camerasData.data, total: camerasData.total });
    });
  }, [filter]);

  const pageChange = useCallback(
    (event: GridPageChangeEvent) => {
      setFilter({ ...filter, skip: event.page.skip, take: event.page.take });
    },
    [filter],
  );

  const content = useMemo(
    () => (
      <div>
        <div className={styles.search}>
          <TextBox
            value={queryText ?? ''}
            onChange={(x) => {
              const val = x.value?.toString();
              setQueryText(val);
              onSearch$.next(val);
            }}
            placeholder='Search by Camera name'
          />
        </div>

          {!!filter && (
              <KendoGrid
                  data={camerasState.data}
                  total={camerasState.total}
                  skip={filter.skip}
                  take={filter.take}
                  pageChange={pageChange}
                  noRecords={<div role={"row"} className={styles.noRecordText}>There are no items to display.</div>}
              >
                <GridColumn title='Id' field='id' />
                <GridColumn
                    title='Type'
                    cell={(props) => (
                        <td>
                          <span>Camera</span>
                        </td>
                    )}
                />

                <GridColumn
                    title='Name'
                    cell={(props) => (
                        <td>
                          <a target='_blank' href={`${settings?.camera_portal_url}/cameras/${props.dataItem.id}`} rel='noreferrer'>
                            {props.dataItem.name}
                          </a>
                        </td>
                    )}
                />
                <GridColumn
                    title='Location'
                    cell={(props) => {
                      const location = locations.find((x) => x.id === props.dataItem.locationId);
                      return (
                          <td>
                            {location ? <Link to={`/locations/${props.dataItem.locationId}`}>{location.name}</Link> : props.dataItem.locationId}
                          </td>
                      );
                    }}
                />
                <GridColumn
                    title='Coordinates'
                    cell={(props) => (
                        <td>
                          <CoordinatesTextShort coordinates={props.dataItem.coordinates} />
                        </td>
                    )}
                />
                <GridColumn title='Floor' field='parkingFloor' />
                <GridColumn title='Source Type' field='streamSource.type' />
                <GridColumn
                    field='streamSource'
                    cell={(props) => (
                        <td>
                          {props.dataItem.streamSource?.type === StreamSourceType.Reolink
                              ? `ID ${props.dataItem.streamSource?.reolinkConfig?.cameraId}`
                              : props.dataItem.streamSource?.type === StreamSourceType.Rtsp
                                  ? props.dataItem.streamSource?.rtspConfig?.url
                                  : `ID ${props.dataItem.streamSource?.nexShieldConfig?.cameraId}`}
                        </td>
                    )}
                    title='Stream Source'
                />
                <GridColumn title='Added on' cell={(props) => <td>{formatter.formatDate(props.dataItem.updatedAt)}</td>} />
                <GridColumn
                    title='Status'
                    cell={(props) => (
                        <td className={styles.status}>
                          {props.dataItem.isActive ? (
                              <span className={styles.active}>Active</span>
                          ) : (
                              <span className={styles.inactive}>Inactive</span>
                          )}
                        </td>
                    )}
                />
              </KendoGrid>
          )}




      </div>
    ),
    [camerasState.data, camerasState.total, filter, formatter, locations, onSearch$, pageChange, queryText, settings?.camera_portal_url],
  );

  return content;
};
